import { useState, useEffect, useId } from 'react'
import { Input } from '@components/ui/Input'
import { Button } from '@components/ui/Button'
import { Search, User, X } from 'lucide-react'

export interface CustomerData {
  name: string
  email: string
  phone: string
  nif: string
}

interface CustomerDataFormProps {
  readonly onSubmit: (data: CustomerData) => void | Promise<void>
  readonly onNifSearch?: (nif: string) => Promise<CustomerData | null>
  readonly isLoading?: boolean
  readonly initialData?: Partial<CustomerData>
  readonly disabled?: boolean
  readonly onClose?: () => void
}

export function CustomerDataForm({
  onSubmit,
  onNifSearch,
  isLoading = false,
  initialData = {},
  disabled = false,
  onClose,
}: Readonly<CustomerDataFormProps>) {
  const [formData, setFormData] = useState<CustomerData>({
    name: initialData.name || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    nif: initialData.nif || '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof CustomerData, string>>>({})
  const [isSearching, setIsSearching] = useState(false)

  const nifId = useId()
  const nameId = useId()
  const emailId = useId()
  const phoneId = useId()

  useEffect(() => {
    if (initialData.nif) {
      setFormData((prev) => ({ ...prev, nif: initialData.nif || '' }))
    }
  }, [initialData])

  const validateNIF = (nif: string): boolean => {
    return /^\d{9}$/.test(nif)
  }

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const validatePhone = (phone: string): boolean => {
    return /^9\d{8}$/.test(phone.replaceAll(/\s/g, ''))
  }

  const handleNifSearch = async () => {
    if (!formData.nif || !validateNIF(formData.nif)) {
      setErrors({ nif: 'NIF deve ter 9 dígitos' })
      return
    }

    if (!onNifSearch) return

    setIsSearching(true)
    setErrors({})
    try {
      const found = await onNifSearch(formData.nif)
      if (found) {
        setFormData(found)
        setErrors({})
      } else {
        setErrors({ nif: 'Cliente não encontrado com este NIF' })
      }
    } catch (error) {
      setErrors({ nif: 'Erro ao pesquisar cliente' })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Partial<Record<keyof CustomerData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    } else if (!/[A-Za-zÀ-ÿ]/.test(formData.name)) {
      newErrors.name = 'Indique um nome válido (com letras)'
    }

    if (formData.email.trim() && !validateEmail(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    if (formData.phone.trim() && !validatePhone(formData.phone)) {
      newErrors.phone = 'Telefone deve ter 9 dígitos começando com 9'
    }

    if (formData.nif.trim() && !validateNIF(formData.nif)) {
      newErrors.nif = 'NIF deve ter 9 dígitos'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white rounded-lg border border-gray-200 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Dados do Cliente</h3>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 -mt-2 mb-2">Obrigatório: nome. Email, telefone e NIF são opcionais.</p>

      <div className="space-y-4">
        <div>
          <label htmlFor={nameId} className="block text-sm font-medium text-gray-700 mb-1">
            Nome <span className="text-red-500">*</span>
          </label>
          <Input
            id={nameId}
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }))
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
            }}
            placeholder="João Silva"
            error={errors.name}
            className="w-full"
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={nifId} className="block text-sm font-medium text-gray-700 mb-1">
            NIF <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                id={nifId}
                type="text"
                value={formData.nif}
                onChange={(e) => {
                  const value = e.target.value.replaceAll(/\D/g, '').slice(0, 9)
                  setFormData((prev) => ({ ...prev, nif: value }))
                  if (errors.nif) setErrors((prev) => ({ ...prev, nif: undefined }))
                }}
                placeholder="123456789"
                maxLength={9}
                error={errors.nif}
                className="w-full"
                disabled={disabled}
              />
            </div>
            {onNifSearch && (
              <Button
                type="button"
                onClick={handleNifSearch}
                disabled={isSearching || !formData.nif || formData.nif.length !== 9 || disabled}
                className="px-4"
              >
                <Search className="w-4 h-4" />
              </Button>
            )}
          </div>
          {onNifSearch && (
            <p className="mt-1 text-xs text-gray-500">Se preencher o NIF com 9 dígitos, pode pesquisar na base de dados.</p>
          )}
        </div>

        <div>
          <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <Input
            id={emailId}
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, email: e.target.value }))
              if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }))
            }}
            placeholder="joao@example.com"
            error={errors.email}
            className="w-full"
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={phoneId} className="block text-sm font-medium text-gray-700 mb-1">
            Telefone <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <Input
            id={phoneId}
            type="tel"
            value={formData.phone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 9)
              setFormData((prev) => ({ ...prev, phone: value }))
              if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }))
            }}
            placeholder="912345678"
            maxLength={9}
            error={errors.phone}
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <Button type="submit" disabled={isLoading || disabled} className="min-w-[120px]">
          {isLoading ? 'A processar...' : 'Continuar'}
        </Button>
      </div>
    </form>
  )
}
